import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { FaUserPlus, FaHashtag, FaUsers, FaFire, FaStar, FaSpinner } from "react-icons/fa";
import { SurveyContext } from "../context/SurveyContext";
import { AuthContext } from "../context/AuthContext";
import { getProfilePicture } from "../utils/avatar";

const WhoToFollow = ({ onInteraction }) => {
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { condition } = useContext(SurveyContext);
  const { user } = useContext(AuthContext);

  const fetchSuggestedUsers = useCallback(async () => {
    try {
      const response = await api.get("/users/suggestions", {
        params: { condition, limit: 3 },
      });
      setSuggestedUsers(response.data);
    } catch (err) {
      setError("Failed to fetch user suggestions");
      console.error("Error fetching user suggestions:", err);
    } finally {
      setLoading(false);
    }
  }, [condition]);

  useEffect(() => {
    fetchSuggestedUsers();
  }, [fetchSuggestedUsers]);

  const handleFollow = async (userId) => {
    try {
      if (!user || !user.userId) {
        console.error("User is not logged in");
        return;
      }
      await api.post(`/follow`, { followeeId: userId });
      onInteraction("follow", userId);
      setSuggestedUsers(suggestedUsers.filter((u) => u._id !== userId));
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to follow user";
      setError(msg);
      console.error("Error following user:", err.response?.data || err.message);
    }
  };

  const getSuggestionIcon = (reason) => {
    switch (reason) {
      case "Followed by people you follow":
        return <FaUsers className="w-3 h-3 text-spiders-primary-400" />;
      case "Similar interests":
        return <FaHashtag className="w-3 h-3 text-spiders-success" />;
      case "Active user":
        return <FaFire className="w-3 h-3 text-spiders-warning" />;
      default:
        return <FaStar className="w-3 h-3 text-spiders-text-muted" />;
    }
  };

  if (loading) {
    return (
      <div className="spiders-card p-4">
        <div className="flex items-center justify-center space-x-2 text-spiders-text-muted">
          <FaSpinner className="animate-spin" />
          <span>Loading suggestions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="spiders-card p-4 text-spiders-error">{error}</div>
    );
  }

  return (
    <div className="spiders-card overflow-hidden">
      <div className="p-4 border-b border-spiders-border">
        <h2 className="text-xl font-bold text-spiders-text-primary">Who to follow</h2>
      </div>

      {suggestedUsers.length === 0 ? (
        <div className="p-4 text-spiders-text-muted text-center">
          No suggestions available at the moment
        </div>
      ) : (
        <div>
          {suggestedUsers.slice(0, 3).map((suggestedUser) => (
            <div
              key={suggestedUser._id}
              className="px-4 py-4 border-b border-spiders-border hover:bg-spiders-bg-elevated/30 transition-colors duration-200"
            >
              <div className="flex space-x-3">
                <Link
                  to={`/profile/${suggestedUser.username}?condition=${condition}`}
                  className="flex-shrink-0"
                >
                  <img
                    src={getProfilePicture(suggestedUser.username)}
                    alt={suggestedUser.username}
                    className="w-12 h-12 rounded-full hover:opacity-90 transition-opacity border-2 border-spiders-border"
                  />
                </Link>

                <div className="flex-1 min-w-0">
                  {/* Name/Handle and Follow Button Row */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0 pr-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link
                          to={`/profile/${suggestedUser.username}?condition=${condition}`}
                          className="text-spiders-text-primary font-bold hover:underline leading-tight text-base"
                        >
                          {suggestedUser.username}
                        </Link>
                      </div>
                      <div className="mt-0.5">
                        <span className="text-spiders-text-muted text-sm">
                          @{suggestedUser.username}
                        </span>
                      </div>
                    </div>

                    {/* Follow Button - Icon only */}
                    <button
                      onClick={() => handleFollow(suggestedUser._id)}
                      className="flex-shrink-0 w-9 h-9 rounded-full bg-spiders-primary-500 hover:bg-spiders-primary-600 text-white flex items-center justify-center transition-colors"
                      title="Follow"
                    >
                      <FaUserPlus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Bio */}
                  {suggestedUser.bio && (
                    <p className="text-spiders-text-secondary text-sm mb-3 leading-relaxed line-clamp-2">
                      {suggestedUser.bio}
                    </p>
                  )}

                  {/* Stats and Meta Info */}
                  <div className="flex items-center space-x-4 text-sm text-spiders-text-muted mb-2">
                    <span>{suggestedUser.followerCount} followers</span>
                    {suggestedUser.recentActivity > 0 && (
                      <>
                        <span>•</span>
                        <div className="flex items-center space-x-1.5">
                          <div className="w-2 h-2 bg-spiders-success rounded-full"></div>
                          <span>{suggestedUser.recentActivity} recent posts</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Suggestion reason */}
                  {suggestedUser.suggestionReason && (
                    <div className="flex items-center space-x-2">
                      {getSuggestionIcon(suggestedUser.suggestionReason)}
                      <span className="text-xs text-spiders-text-muted font-medium">
                        {suggestedUser.suggestionReason}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Refresh suggestions */}
      <div className="p-4 border-t border-spiders-border">
        <button
          onClick={fetchSuggestedUsers}
          className="text-spiders-primary-400 hover:text-spiders-primary-300 text-sm font-medium transition-colors"
          disabled={loading}
        >
          {loading ? "Loading..." : "Show more"}
        </button>
      </div>
    </div>
  );
};

export default WhoToFollow;
